import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/common/Logo";
import { FullScreenLoading } from "@/components/common/Loading";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

/**
 * Atteinte via le lien email envoyé par /auth/forgot-password.
 *
 * Supabase parse automatiquement le hash (#access_token=…&refresh_token=…
 * &type=recovery) au montage du client et établit une session de type
 * "recovery". On peut alors appeler updateUser({ password }) pour
 * définir le nouveau mot de passe.
 *
 * Si l'utilisateur arrive ici sans token (refresh, lien expiré) on
 * redirige vers /welcome après affichage d'un message.
 */
export function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [hasValidSession, setHasValidSession] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Attendre que supabase parse le hash et établisse la session
    let unsub = () => {};
    const sub = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || (event === "INITIAL_SESSION" && session)) {
        setHasValidSession(true);
        setChecking(false);
      }
    });
    unsub = sub.data.subscription.unsubscribe;

    // Fallback : si après 1.5s on n'a toujours rien, on vérifie une dernière
    // fois la session puis on coupe le loader.
    const fallback = setTimeout(async () => {
      const { data } = await supabase.auth.getSession();
      setHasValidSession(!!data.session);
      setChecking(false);
    }, 1500);

    return () => {
      unsub();
      clearTimeout(fallback);
    };
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password.length < 8) {
      toast.error(t("auth.errors.passwordTooShort"));
      return;
    }
    if (password !== confirm) {
      toast.error(t("auth.errors.passwordMismatch"));
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      toast.success(t("auth.passwordUpdated"));
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur");
      setSubmitting(false);
    }
  }

  if (checking) return <FullScreenLoading />;

  if (!hasValidSession) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <Logo size="sm" />
        <h1 className="mt-6 text-xl font-bold tracking-tight">Lien invalide ou expiré</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Demande un nouveau lien depuis l'écran de connexion.
        </p>
        <Button asChild className="mt-6" size="lg">
          <a href="/welcome">{t("auth.signIn")}</a>
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col safe-top safe-bottom px-6 py-8">
      <Logo size="sm" />

      <div className="flex-1 max-w-md w-full mx-auto mt-10">
        <h1 className="text-2xl font-bold tracking-tight">{t("auth.resetPasswordTitle")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("auth.passwordHint")}</p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-xs">{t("auth.password")}</Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm" className="text-xs">{t("auth.passwordConfirm")}</Label>
            <Input
              id="confirm"
              type="password"
              autoComplete="new-password"
              placeholder="••••••••"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
            />
          </div>
          <Button type="submit" size="lg" className="w-full" loading={submitting}>
            {t("auth.saveNewPassword")}
          </Button>
        </form>
      </div>
    </div>
  );
}
